import React, { useState } from 'react';
import { CardStats, DateRangeFilter, TimeRangeValue } from '@/components';
import { DashboardResource } from '@/hooks/useDashboardResource';
import { RentalDashboardSummary } from '@/types/rental';
import { formatRupiahCompact } from '@/utils/format';
import {
  formatCollectionRate,
  formatOutstanding,
  formatSignedCount,
  rateTrend,
  resourceCardProps,
} from '../dashboardDisplay';

interface PerformaCardsProps {
  resource: DashboardResource<RentalDashboardSummary>;
  onRetry: () => void;
  onDateRangeChange: (range: { from: Date | null; to: Date | null }) => void;
}

/**
 * The windowed half of the dashboard: how the money and the contract base moved
 * over a period.
 *
 * The date filter lives INSIDE this block rather than above the whole screen,
 * because it governs only these cards. Sitting above everything it implied it
 * governed the fleet counts and the queues too, which it never did.
 */
export const PerformaCards: React.FC<PerformaCardsProps> = ({
  resource,
  onRetry,
  onDateRangeChange,
}) => {
  // The preset last picked; the server's default window matches none of them.
  const [preset, setPreset] = useState<TimeRangeValue>('custom');
  const summary = resource.data;
  const rate = summary?.collectionRate ?? null;
  const previous = summary?.previousCollectionRate ?? null;
  const net = summary?.netContracts ?? null;

  // Each card fails on its own leg, not on the request: the backend answers
  // under Promise.allSettled, so a null block among populated ones is normal.
  const rateProps = resourceCardProps(resource, rate, onRetry);
  const netProps = resourceCardProps(resource, net, onRetry);

  // Computed once. It decides the figure, the colour and the subtitle, and
  // three separate subtractions is three chances for one of them to be
  // rewritten without the others.
  const outstanding = rate ? rate.dueAmount - rate.paidAmount : null;
  const overpaid = outstanding !== null && outstanding < 0;

  return (
    <section aria-labelledby="performa-heading" className="mb-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2
            id="performa-heading"
            className="text-[15px] font-semibold text-gray-900"
          >
            Performa
          </h2>
          <p className="text-[13px] text-gray-500">
            Periode:{' '}
            <span className="font-medium text-gray-700">
              {rate ? `${rate.startDate} s.d. ${rate.endDate}` : '…'}
            </span>
          </p>
          {/* Accrual, not cash: windowed on the charge's date, so NOT the
              Pembayaran menu's figure. */}
          <p className="text-[12px] text-gray-400">
            Collection = tagihan jatuh tempo &amp; lunas dalam periode, bukan
            uang masuk (lihat menu Pembayaran)
          </p>
        </div>
        {/* Remounted per echoed window, since DateRangeFilter reads initialRange
            once. The label is the preset last picked; the server's own default
            (30 days ending yesterday) matches no preset, so it starts as Custom. */}
        <DateRangeFilter
          key={rate ? `${rate.startDate}:${rate.endDate}` : 'pending'}
          initialTimeRange={preset}
          initialRange={{
            from: rate ? new Date(rate.startDate) : null,
            to: rate ? new Date(rate.endDate) : null,
          }}
          onDateRangeChange={(range, timeRange) => {
            setPreset(timeRange);
            onDateRangeChange(range);
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Rate, Total and Target on one card: the rate is only readable next
            to the two amounts it divides. */}
        <CardStats
          size="small"
          title="Collection"
          value={rate ? formatCollectionRate(rate.rate) : '-'}
          icon="ri-percent-line"
          color="green"
          {...rateProps}
          trend={rateTrend(rate?.rate, previous?.rate)}
          subtitle={
            rate
              ? `${formatRupiahCompact(rate.paidAmount)} dari ${formatRupiahCompact(rate.dueAmount)}`
              : undefined
          }
        />
        <CardStats
          size="small"
          title="Masih Harus Ditagihkan"
          // Never clamped to zero — see formatOutstanding for why.
          value={outstanding !== null ? formatOutstanding(outstanding) : '-'}
          icon="ri-bill-line"
          color={overpaid ? 'green' : 'red'}
          {...rateProps}
          subtitle={
            overpaid
              ? 'kelebihan bayar dalam periode'
              : 'Target − Total dalam periode'
          }
        />
        <CardStats
          size="small"
          title="Net Kontrak"
          value={net ? formatSignedCount(net.net) : '-'}
          icon="ri-line-chart-line"
          color={net && net.net < 0 ? 'red' : 'blue'}
          {...netProps}
          subtitle={
            net ? `${net.newCount} baru, ${net.endedCount} berakhir` : undefined
          }
        />
      </div>
    </section>
  );
};

export default PerformaCards;
